import { useEffect, useState } from 'react';
import { t } from '@lingui/macro';
import { isEmpty } from 'lodash';

import Panel from '../../page-framework/panel/PanelDecorator';
import {
    PANEL_CATEGORIES,
    PANEL_PRIORITIES,
    STATUS_LIST_PANEL_PREVIEW_IMAGE,
} from '../../page-framework/panel/PanelRegistry';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { useQueryLoader } from '../../data-table/Loaders';
import { filterId as agentFilterId } from '../../filters/agent-filter/AgentFilter';
import { LegacyIcon } from '../../icon';
import ReportingDataTable from '../../data-table/ReportingDataTable';
import { businessServiceFilterId, siteFilterId, statusFilterId, timeRangeFilterId } from '../../filters';
import useNg1DrillDownService from '../../../services/Ng1DrillDownService';
import { Ng1DrillDownButton } from '../../ng1-drill-down/Ng1DrillDownButton';
import { DEFAULT_INVALID_VALUE } from '../../../utilities/FormattingUtilities';

const title = t`nPoints`;

const dataSource = {
    url: '/query/table',
    params: {
        type: 'agent',
        entityFields: 'name,tags',
        metrics: ['status', 'statusCounts'],
        trends: ['status'],
        renderAsEntity: true,
    },
};

const DEFAULT_COLUMNS = [
    {
        type: COLUMN_TYPES.ICON,
        title: t`Type`,
        width: 74,
        mapping: 'agent.typeIcon',
        sortable: true,
        csvFormatter: (value, row) => row?.agent?.translatedType,
        icons: [
            {
                getIcon: (row) => <LegacyIcon icon={row?.agent?.typeIcon} />,
                getTooltip: (row) => row?.agent?.translatedType,
            },
        ],
    },
    {
        type: COLUMN_TYPES.DRILLINLINK,
        title: t`nPoint`,
        sortable: true,
        mapping: 'agent.name',
        width: 250,
        link: {
            target: '/agent-detail',
            mappedFilterParams: {
                [agentFilterId]: 'agent',
            },
        },
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Tags`,
        sortable: false,
        mapping: 'agent.tags',
        formatter: (tags) => {
            if (isEmpty(tags)) return DEFAULT_INVALID_VALUE;
            return tags.join(', ');
        },
    },
    {
        title: t`Impacted Service Tests`,
        width: 194,
        sortable: true,
        align: 'center',
        sortProperty: 'DIVIDE(statusCounts.affected, statusCounts.total)',
        mapper: (row) => `${row.statusCounts?.affected} / ${row.statusCounts?.total}`,
    },
    {
        type: COLUMN_TYPES.STATUSTREND,
        title: t`Status Trend`,
        sortable: true,
        sortProperty: 'status',
        mapping: 'trends.status.data',
    },
];

const NG1_DRILLDOWN_COLUMN = {
    type: COLUMN_TYPES.ICON,
    mapping: 'agent',
    width: 30,
    uiOnly: true,
    icons: [
        {
            getIcon: (row) => {
                return <Ng1DrillDownButton agent={row?.agent} />;
            },
            getTooltip: () => t`Launch nGeniusONE Smart Edge Service Monitor`,
        },
    ],
};

const sorters = [{ property: 'status', direction: 'desc' }];

function AgentListPanel(props) {
    const loader = useQueryLoader(dataSource);
    const { hasNg1DrillDownPrivilege } = useNg1DrillDownService();
    const [columns, setColumns] = useState(DEFAULT_COLUMNS);

    useEffect(() => {
        if (hasNg1DrillDownPrivilege()) {
            setColumns([...DEFAULT_COLUMNS, NG1_DRILLDOWN_COLUMN]);
        }
    }, [hasNg1DrillDownPrivilege]);

    return (
        <ReportingDataTable
            panelProps={props}
            loader={loader}
            columns={columns}
            sorters={sorters}
            deletedMapping="agent.deleted"
        />
    );
}

AgentListPanel.propTypes = panelPropTypes;

AgentListPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    autoHeight: true,
    minContentHeight: 130,
};

export default Panel({
    typeAlias: 'AgentList',
    catalogEntry: {
        category: PANEL_CATEGORIES.AGENT,
        priority: PANEL_PRIORITIES.LIST,
        title,
        description: t`List of nPoints, showing status trend and impacted service tests`,
        previewImage: STATUS_LIST_PANEL_PREVIEW_IMAGE,
    },
    filters: {
        required: [timeRangeFilterId],
        optional: [businessServiceFilterId, siteFilterId, statusFilterId],
    },
})(AgentListPanel);
